import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { Card, Data, Navigation } from 'ui'
import { ProfileMatch } from '../queries/types'

const { Attribute } = Data
const { SimplePaginationBar } = Navigation

interface MatchesListProps {
  matches?: {
    data?: ProfileMatch[]
    pagination?: {
      totalItems?: number
      totalPages?: number
      currentPage?: number
      pageSize?: number
    }
  }
  isLoading?: boolean
  error?: unknown
  className?: string
  onPageChange?: (page: number) => void
}

export const MatchesList: React.FC<MatchesListProps> = ({
  matches,
  isLoading,
  error,
  className = '',
  onPageChange,
}) => {
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(
    matches?.pagination?.currentPage ?? 1
  )

  if (isLoading) {
    return <div className={className}>Loading matches...</div>
  }

  if (error) {
    return (
      <div className={className}>
        {error instanceof Error ? error.message : 'Failed to load matches'}
      </div>
    )
  }

  const totalMatches = matches?.pagination?.totalItems ?? 0
  const totalPages = matches?.pagination?.totalPages ?? 1

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    if (onPageChange) {
      onPageChange(page)
    }
  }

  return (
    <div className={`${className}`}>
      <span className="my-4 border-top border-stone-200 w-full block" />
      <Attribute
        label={`${totalMatches} Match${totalMatches !== 1 ? 'es' : ''}`}
      />
      <div className="w-full">
        {matches?.data?.map((match) => (
          <Card.ExtendedProfile
            key={match.id}
            profile={{
              id: match.id,
              name: match.name,
              title: match.title,
              color: match.color,
              avatarUrl: match.avatarUrl,
              keywords: match.keywords,
              hasUnreadMessages: false,
            }}
            matchDescription={
              match.publicBio ??
              match.about ??
              'Strong match based on profile criteria'
            }
            matchPercent={match.matchScore?.relevance ?? 0}
            favorite={match.favorite}
            className="px-2 py-3 mt-2 border border-body/5"
            onSelectProfile={(profileId) =>
              navigate(`/app/matches/${profileId}`)
            }
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-4">
          <SimplePaginationBar
            currentPage={currentPage}
            pageCount={totalPages}
            onPageClick={handlePageChange}
            maxPages={5}
          />
        </div>
      )}
    </div>
  )
}
